import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthProvider";

/**
 * Holds the logo generator's form fields and its last batch of concepts.
 *
 * Why this needs to exist: LogoGeneratorPage used to keep this in local
 * component state, which React throws away the moment the page unmounts —
 * so clicking "Edit" on a concept, then "Back to concepts", landed on an
 * empty form with the other nine concepts gone. Lifting it into a
 * provider (same shape as SearchProvider) keeps it alive across that
 * navigation, and mirrors it into sessionStorage so a reload doesn't lose
 * it either.
 *
 * Scoped per signed-in user for the same reason SearchProvider is:
 * sessionStorage survives a logout, so without this a different person
 * signing in on the same tab would see the previous person's brand name
 * and generated logos.
 */

const LogoContext = createContext(null);
const STORAGE_KEY = "gmft:logo-studio";

const DEFAULT_STUDIO = {
  brandName: "",
  color: "#7C3AED",
  useColor: true,
  shape: "none",
  style: "minimalist",
  background: "transparent",
  logos: [],
  hasGenerated: false,
};

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(entry) {
  try {
    if (entry) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage full or blocked — the in-memory copy still works.
  }
}

export function LogoProvider({ children }) {
  const { user } = useAuth();
  const currentUserId = user?.id || null;

  const [studio, setStudioState] = useState(DEFAULT_STUDIO);
  const knownUserId = useRef(undefined);

  useEffect(() => {
    if (knownUserId.current === currentUserId) return;

    const isFirstCheck = knownUserId.current === undefined;
    knownUserId.current = currentUserId;

    const stored = isFirstCheck ? readStored() : null;
    const belongsToCurrentUser =
      stored && currentUserId && stored.ownerId === currentUserId;

    if (belongsToCurrentUser) {
      setStudioState({ ...DEFAULT_STUDIO, ...stored.studio });
      return;
    }

    // Different account (or no account) — never carry another user's
    // brand name or concepts across the switch.
    setStudioState(DEFAULT_STUDIO);
    writeStored(null);
  }, [currentUserId]);

  const updateStudio = useCallback(
    (patch) => {
      setStudioState((prev) => {
        const next = {
          ...prev,
          ...(typeof patch === "function" ? patch(prev) : patch),
        };
        writeStored({ ownerId: currentUserId, studio: next });
        return next;
      });
    },
    [currentUserId],
  );

  const resetStudio = useCallback(() => {
    setStudioState(DEFAULT_STUDIO);
    writeStored(null);
  }, []);

  return (
    <LogoContext.Provider value={{ studio, updateStudio, resetStudio }}>
      {children}
    </LogoContext.Provider>
  );
}

export function useLogoStudio() {
  const context = useContext(LogoContext);
  if (!context)
    throw new Error("useLogoStudio must be used inside a LogoProvider.");
  return context;
}
